/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/app/modules/Auth/pages/AuthPage`, `src/app/BasePage`).
 */
import * as Sentry from '@sentry/react'
import VerifyEmailPage from 'app/modules/auth/components/VerifyEmailPage'
import {
  clearMustCreatePassword,
  clearMustCreatePasswordStorage,
} from 'app/modules/auth/selective/mustCreatePasswordHelpers'
import ExternalRecommendation from 'app/pages/school/CollegeCounselling/components/ExternalRecommendation'
import VendorRegisterForm from 'app/pages/school/vendor/components/RegistrationForm'
import {isSelectiveAiDomains} from 'common/HelperFunctions'
import SuspensedView from 'common/components/SuspensedView'
import FullLayout from 'common/layout/FullLayout'
import {FC, lazy, useEffect} from 'react'
import {shallowEqual, useDispatch, useSelector} from 'react-redux'
import {BrowserRouter, Navigate, Outlet, Route, Routes, useLocation} from 'react-router'
import {RootState} from 'setup'

import {App} from '../App'
import {AuthPage, Logout} from '../modules/auth'
import {AuthLayout as OldAuthLayout} from '../modules/auth/AuthLayout'
import ChangeGeneratedPassword from '../modules/auth/components/ChangeGeneratedPassword'
import CreatePassword from '../modules/auth/components/CreatePassword'
import {AuthLayout as NewAuthLayout} from '../modules/auth/new/AuthLayout'
import {UniversalLoginRoute} from '../modules/auth/universal/UniversalLoginRoute'
import {ErrorsPage} from '../modules/errors/ErrorsPage'
import SchoolRoutes from './SchoolRoutes'
import StudentRoutes from './StudentRoutes'

/**
 * Base URL of the website.
 *
 * @see https://facebook.github.io/create-react-app/docs/using-the-public-folder
 */
const {BASE_URL} = import.meta.env

const Stats = lazy(() => import('app/pages/school/Stats'))
const ChatbotAnalytics = lazy(
  () => import('app/pages/school/ChatbotAnalytics/ChatbotAnalyticsPage')
)

// const AdmissionsLandingPage = lazy(() => import('app/pages/admissions/AdmissionsLandingPage'))
const AdmissionsAuthPage = lazy(() => import('app/pages/admissions/AdmissionsAuthPage'))
const AdmissionsDashboardPage = lazy(() => import('app/pages/admissions/AdmissionsDashboardPage'))
const AdmissionsApplyPage = lazy(() => import('app/pages/admissions/AdmissionsApplyPage'))
const EmailVerificationGate = lazy(() =>
  import('app/pages/admissions/components/EmailVerificationGate').then((m) => ({
    default: m.EmailVerificationGate,
  }))
)
const AdmissionsElementsPage = lazy(() => import('app/pages/admissions/AdmissionsElementsPage'))

const RequirePasswordCreation: FC = () => {
  const location = useLocation()
  const isLoggedIn = useSelector<RootState>(
    (state) => state.userReducer.isLoggedIn,
    shallowEqual
  ) as boolean

  const mustCreatePasswordFromRedux = useSelector<RootState>(
    (state) => (state.userReducer as any).mustCreatePassword,
    shallowEqual
  ) as boolean

  useEffect(() => {
    if (isLoggedIn && !mustCreatePasswordFromRedux) {
      clearMustCreatePasswordStorage()
    }
  }, [isLoggedIn, mustCreatePasswordFromRedux])

  // /logout must stay reachable: it is nested under this guard, and a user
  // holding a system-generated password they do not want to replace right now
  // (wrong account, shared device) would otherwise be trapped on the page.
  const isExemptPath =
    location.pathname === '/create-password' || location.pathname.startsWith('/logout')
  if (isLoggedIn && mustCreatePasswordFromRedux && !isExemptPath) {
    return <Navigate to='/create-password' replace />
  }

  return <Outlet />
}

const CreatePasswordPage: FC = () => {
  const dispatch = useDispatch()
  const isLoggedIn = useSelector<RootState>(
    (state) => state.userReducer.isLoggedIn,
    shallowEqual
  ) as boolean

  const mustCreatePassword = useSelector<RootState>(
    (state) => (state.userReducer as any).mustCreatePassword,
    shallowEqual
  ) as boolean

  if (!isLoggedIn) {
    return <Navigate to='/auth' replace />
  }

  if (!mustCreatePassword) {
    return <Navigate to='/dashboard?step=0' replace />
  }

  const onSuccess = () => clearMustCreatePassword(dispatch)

  // The selective-AI product has its own branded screen; classic tenants get
  // the Metronic form inside the regular auth layout (see CreatePasswordLayout).
  return isSelectiveAiDomains() ? (
    <CreatePassword onSuccess={onSuccess} />
  ) : (
    <ChangeGeneratedPassword onSuccess={onSuccess} />
  )
}

/**
 * Same chrome as the login page for classic tenants: school logo, background
 * image and card, honouring the "new login screen" setting like AuthPage does.
 */
const CreatePasswordLayout: FC = () => {
  const newScreens = useSelector<RootState>(
    (state) => (state.mainReducer as any)?.generalSettings?.new_login_screen,
    shallowEqual
  ) as boolean
  return newScreens ? <NewAuthLayout /> : <OldAuthLayout />
}

const AppRoutes: FC = () => {
  const isLoggedIn = useSelector<RootState>(
    (state) => state.userReducer.isLoggedIn,
    shallowEqual
  ) as boolean
  const userType = useSelector<RootState>(
    (state) => state.userReducer.userType,
    shallowEqual
  ) as string
  const isAdmissionUser = useSelector<RootState>(
    (state) => state.userReducer.isAdmissionUser,
    shallowEqual
  ) as boolean

  const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes)

  const url = new URL(window.location.href)
  const searchParams = new URLSearchParams(url.search)
  const path = searchParams.get('path')

  return (
    <BrowserRouter basename={BASE_URL}>
      <SentryRoutes>
        <Route element={<App />}>
          <Route path='error/*' element={<ErrorsPage />} />

          <Route element={<FullLayout />}>
            <Route path='/institute/stats/*' element={<Stats />} />
            <Route path='/institute/chatbot-analytics/*' element={<ChatbotAnalytics />} />
            <Route path='/institute/vendors/register/:id' element={<VendorRegisterForm />} />
            {/* External recommenders have no account — they reach this from an
                emailed link, so it must stay above the !isLoggedIn catch-all
                below or it would redirect them to the login page. */}
            <Route
              path='/institute/counselling/recommendation/:token'
              element={<ExternalRecommendation />}
            />
          </Route>

          {/* Always mounted: the "new admission form" setting only decides whether the
              login page links here; the APIs still gate on admission_form_permission. */}
          <>
            <Route
              path='admissions'
              element={
                <SuspensedView>
                  {/* <AdmissionsLandingPage /> */}
                  <AdmissionsAuthPage />
                </SuspensedView>
              }
            />
            <Route
              path='admissions/auth'
              element={
                <SuspensedView>
                  <AdmissionsAuthPage />
                </SuspensedView>
              }
            />
            {/* the emailed forgot-password link: ?code= opens the reset mode */}
            <Route
              path='admissions/reset-password'
              element={
                <SuspensedView>
                  <AdmissionsAuthPage />
                </SuspensedView>
              }
            />
            <Route
              path='admissions/dashboard'
              element={
                <SuspensedView>
                  <EmailVerificationGate>
                    <AdmissionsDashboardPage />
                  </EmailVerificationGate>
                </SuspensedView>
              }
            />
            <Route
              path='admissions/apply'
              element={
                <SuspensedView>
                  <EmailVerificationGate>
                    <AdmissionsApplyPage />
                  </EmailVerificationGate>
                </SuspensedView>
              }
            />
            <Route
              path='admissions/elements'
              element={
                <SuspensedView>
                  <AdmissionsElementsPage />
                </SuspensedView>
              }
            />
          </>

          <Route path='login' element={<UniversalLoginRoute />} />
          {isSelectiveAiDomains() ? (
            <Route path='create-password' element={<CreatePasswordPage />} />
          ) : (
            <Route element={<CreatePasswordLayout />}>
              <Route path='create-password' element={<CreatePasswordPage />} />
            </Route>
          )}

          <Route element={<FullLayout />}>
            <Route path='verify-email' element={<VerifyEmailPage />} />
          </Route>

          <Route element={<RequirePasswordCreation />}>
            <Route path='auth/*' element={<AuthPage />} />
            {!isLoggedIn && (
              <Route
                path='*'
                element={<Navigate replace to={`/auth?path=${location.pathname}`} />}
              />
            )}

            <Route path='logout' element={<Logout />} />

            {isLoggedIn &&
              userType &&
              /* An in-progress applicant logs in as an INACTIVE student, so the
                 branch below would otherwise drop them into the full student
                 portal at /dashboard - which their token cannot serve (admission
                 tokens are only accepted by the admission APIs) and which the
                 saga's global 401-logout is deliberately disabled for. Keep them
                 inside /admissions/*; those routes are declared above and route
                 ranking matches them before this catch-all, so it cannot loop. */
              (isAdmissionUser ? (
                <Route path='/*' element={<Navigate to='/admissions/dashboard' replace />} />
              ) : userType === 'STUDENT' || userType === 'PARENT' ? (
                <>
                  <Route
                    path='/*'
                    element={
                      <SuspensedView>
                        <StudentRoutes />
                      </SuspensedView>
                    }
                  />
                  <Route index element={<Navigate replace to={path || '/dashboard'} />} />
                </>
              ) : userType === 'TEACHER' || userType === 'STAFF' || userType === 'ADMIN' ? (
                <>
                  <Route
                    path='/*'
                    element={
                      <SuspensedView>
                        <SchoolRoutes />
                      </SuspensedView>
                    }
                  />
                  <Route index element={<Navigate replace to={path || '/dashboard'} />} />
                </>
              ) : (
                <></>
              ))}
          </Route>

          <Route path='*' element={<ErrorsPage />} />
        </Route>
      </SentryRoutes>
    </BrowserRouter>
  )
}

export {AppRoutes}
